import { ApiError, createAction } from 'redux-api-middleware';
import Strings from '../../assets/strings';
import { RemoteImage } from '../../common/Types';
import { endpointWithUserId } from '../../common/url';
import { State as StoreState } from '../types/State';
import { HTTP_GET } from './ApiAction';
import { ListType } from './BonusActivity';
import { isMainInsuredActive } from './Dashboard';

export const LOAD_BONUS_REWARDS = 'barmer-bonus-shared/shop/bonus/rewards/LOAD' as const;
export const LOAD_BONUS_REWARDS_SUCCESS = 'barmer-bonus-shared/shop/bonus/rewards/SUCCESS' as const;
export const LOAD_BONUS_REWARDS_FAILURE = 'barmer-bonus-shared/shop/bonus/rewards/FAILURE' as const;
export const RESET_BONUS_REWARDS = 'barmer-bonus-shared/shop/bonus/rewards/RESET' as const;

type LoadBonusRewards = { type: typeof LOAD_BONUS_REWARDS; payload?: ApiError; error?: boolean };
type BonusRewardsUpdate = { type: typeof LOAD_BONUS_REWARDS_SUCCESS; payload: Array<BonusReward> };
type BonusRewardsUpdateFailure = {
  type: typeof LOAD_BONUS_REWARDS_FAILURE;
  payload?: ApiError;
  error?: boolean;
};
type ResetBonusRewards = { type: typeof RESET_BONUS_REWARDS };

export type Action =
  | LoadBonusRewards
  | BonusRewardsUpdate
  | BonusRewardsUpdateFailure
  | ResetBonusRewards;

export const BONUS_REWARDS_LOADING = 'loading' as const;
export const BONUS_REWARDS_LOADED = 'loaded' as const;
export const BONUS_REWARDS_ERROR = 'error' as const;
export const BONUS_REWARDS_UNKNOWN = 'unknown' as const;

export type LoadState =
  | typeof BONUS_REWARDS_LOADING
  | typeof BONUS_REWARDS_LOADED
  | typeof BONUS_REWARDS_ERROR
  | typeof BONUS_REWARDS_UNKNOWN;

export const PHYSICAL_REWARD = 'PHYSICAL' as const;
export const MONEY_REWARD = 'MONEY' as const;
export const COUPON_REWARD = 'COUPON' as const;
export const DONATION_REWARD = 'DONATION' as const;
export const GIFT_REWARD = 'GIFT' as const;
export const SUBVENTION_REWARD = 'SUBVENTION' as const;

export type RewardType =
  | typeof PHYSICAL_REWARD
  | typeof MONEY_REWARD
  | typeof COUPON_REWARD
  | typeof DONATION_REWARD
  | typeof GIFT_REWARD
  | typeof SUBVENTION_REWARD;

type BonusRewardMoney = {
  articleNumber: string;
  description: Array<BonusRewardDescription>;
  id: number;
  name: string;
  category: RewardCategory;
  type: typeof MONEY_REWARD | typeof DONATION_REWARD;
  pointCost: number;
  isAvailableToUser: boolean;
  userHasClaim: boolean;
  availabilityState: RewardAvailabilityState | null | undefined;
  valueInEUR: number;
  image: RemoteImage;
};

type BonusRewardNoMoney = {
  articleNumber: string;
  description: Array<BonusRewardDescription>;
  id: number;
  name: string;
  category: RewardCategory;
  type:
    | typeof PHYSICAL_REWARD
    | typeof COUPON_REWARD
    | typeof GIFT_REWARD
    | typeof SUBVENTION_REWARD;
  pointCost: number;
  isAvailableToUser: boolean;
  userHasClaim: boolean;
  availabilityState: RewardAvailabilityState | null | undefined;
  image: RemoteImage;
};

export type BonusReward = BonusRewardMoney | BonusRewardNoMoney;

export type BonusRewardDescription = {
  headline?: string;
  content?: string;
  list?: ListType;
};

export const ALL_CATEGORIES = 'AllCategories' as const;
export const SPORTS = 'Sports' as const;
export const HEALTH = 'Health' as const;
export const NUTRITION = 'Nutrition' as const;
export const ELECTRONICS = 'Electronics' as const;
export const FUN = 'Fun' as const;
export const DONATION = 'Donation' as const;
export const MONEY = 'Money' as const;
export const SUBVENTION = 'Subvention' as const;

// Arvato calls this reward group
export type RewardCategory =
  | typeof ALL_CATEGORIES
  | typeof SPORTS
  | typeof HEALTH
  | typeof NUTRITION
  | typeof ELECTRONICS
  | typeof FUN
  | typeof DONATION
  | typeof MONEY
  | typeof SUBVENTION;

export const categoryOrder: Array<RewardCategory> = [
  HEALTH,
  SPORTS,
  NUTRITION,
  ELECTRONICS,
  FUN,
  MONEY,
];

export const AVAILABLE = 'AVAILABLE' as const;
export const NO_CLAIM = 'NO_CLAIM' as const;
export const NO_AVAILABLE_CLAIM_TYPE = 'NO_AVAILABLE_CLAIM_TYPE' as const;
export const NO_1500_CLAIM = 'NO_1500_CLAIM' as const;
export const POINTS_MISSING = 'POINTS_MISSING' as const;

export type RewardAvailabilityState =
  | typeof AVAILABLE
  | typeof NO_CLAIM
  | typeof NO_AVAILABLE_CLAIM_TYPE
  | typeof NO_1500_CLAIM
  | typeof POINTS_MISSING;

export type State = {
  loadingState: LoadState;
  bonusRewards: Array<BonusReward>;
  error?: ApiError;
};

const initialState: State = {
  loadingState: BONUS_REWARDS_UNKNOWN,
  bonusRewards: [],
  error: undefined,
};

export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case LOAD_BONUS_REWARDS:
      return {
        ...state,
        loadingState: action.error === undefined ? BONUS_REWARDS_LOADING : BONUS_REWARDS_ERROR,
        error: action.error ? action.payload : undefined,
      };

    case LOAD_BONUS_REWARDS_SUCCESS:
      return {
        ...state,
        loadingState: BONUS_REWARDS_LOADED,
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        bonusRewards: addAvailabilityState(action.payload),
      };

    case LOAD_BONUS_REWARDS_FAILURE:
      return {
        ...state,
        loadingState: BONUS_REWARDS_ERROR,
        error: action.payload,
      };

    case RESET_BONUS_REWARDS:
      return initialState;

    default:
      return state;
  }
}

export const loadBonusRewards = (userId: string) =>
  createAction({
    endpoint: endpointWithUserId('/shop/bonus/rewards', userId),
    types: [LOAD_BONUS_REWARDS, LOAD_BONUS_REWARDS_SUCCESS, LOAD_BONUS_REWARDS_FAILURE],
    method: HTTP_GET,
  });

export const areBonusRewardsLoaded = (globalState: StoreState): boolean => {
  return (
    globalState.bonusShop &&
    (BONUS_REWARDS_LOADED === globalState.bonusShop.loadingState ||
      BONUS_REWARDS_ERROR === globalState.bonusShop.loadingState)
  );
};

export const areBonusRewardsLoading = (globalState: StoreState): boolean => {
  return globalState.bonusShop && BONUS_REWARDS_LOADING === globalState.bonusShop.loadingState;
};

export const areBonusRewardsUnknown = (globalState: StoreState): boolean => {
  return globalState.bonusShop && BONUS_REWARDS_UNKNOWN === globalState.bonusShop.loadingState;
};

// TODO: not very accurate name since we introduced claims to FE logic => we should change the name
export const userHasRewardClaim = (globalState: StoreState): boolean => {
  return globalState.bonusShop.bonusRewards.some((x) => x.isAvailableToUser);
};

export const userHasAnyClaim = (globalState: StoreState): boolean => {
  return globalState.bonusShop.bonusRewards.some((x) => x.userHasClaim);
};

export const userHasNonSubventionClaim = (globalState: StoreState): boolean => {
  return globalState.bonusShop.bonusRewards.some(
    (x) => x.userHasClaim && x.type !== SUBVENTION_REWARD,
  );
};

export const addAvailabilityState = (rewards: Array<BonusReward>): Array<BonusReward> => {
  const doesUserHaveAnyClaim = rewards.some((x) => x.userHasClaim);
  const doesUserHaveNonSubventionClaim = rewards.some(
    (x) => x.userHasClaim && x.type !== SUBVENTION_REWARD,
  );

  return rewards.map((reward: BonusReward) => {
    let state: RewardAvailabilityState = AVAILABLE;
    if (!reward.isAvailableToUser) {
      if (!doesUserHaveAnyClaim) {
        state = NO_CLAIM;
      } else if (reward.userHasClaim) {
        state = POINTS_MISSING;
      } else if (doesUserHaveNonSubventionClaim && reward.pointCost === 1500) {
        state = NO_1500_CLAIM;
      } else {
        state = NO_AVAILABLE_CLAIM_TYPE;
      }
    }

    return {
      ...reward,
      availabilityState: state,
    };
  });
};

export const warningForRewardAvailabilityState = (
  state: RewardAvailabilityState,
  type: RewardType,
): string | null | undefined => {
  switch (state) {
    case NO_CLAIM:
      return Strings.bonusShop.rewardWarnings.noClaim;
    case NO_1500_CLAIM:
      return Strings.bonusShop.rewardWarnings.no1500;
    case POINTS_MISSING:
      return Strings.bonusShop.rewardWarnings.pointsMissing;
    case NO_AVAILABLE_CLAIM_TYPE:
      switch (type) {
        case SUBVENTION_REWARD:
          return Strings.bonusShop.rewardWarnings.noSubvention;
        case DONATION_REWARD:
          return Strings.bonusShop.rewardWarnings.noDonation;
        case GIFT_REWARD:
          return Strings.bonusShop.rewardWarnings.noGift;
        default:
          return Strings.bonusShop.rewardWarnings.noPhysical;
      }
    default:
      return null;
  }
};

export const getSortedAvailableCategories = (globalState: StoreState): Array<RewardCategory> => {
  if (globalState.bonusShop) {
    const uniqueCategories = new Set(
      Array.from(
        new Set(globalState.bonusShop.bonusRewards.map((value: BonusReward) => value.category)),
      ),
    );
    const categories = categoryOrder.filter((value) => uniqueCategories.has(value));
    categories.unshift(ALL_CATEGORIES);
    return categories;
  }

  return [];
};

export const rewardWillBeShipped = (reward: BonusReward): boolean => {
  return reward.type === PHYSICAL_REWARD || reward.type === COUPON_REWARD;
};

// / all rewards minus those on the dashboard and the duplicated money rewards
export const getFilteredRewards = (globalState: StoreState): Array<BonusReward> => {
  return globalState.bonusShop.bonusRewards.filter(
    (value: BonusReward) =>
      value.type !== DONATION_REWARD &&
      value.type !== SUBVENTION_REWARD &&
      value.type !== GIFT_REWARD &&
      !(value.type === MONEY_REWARD && value.pointCost !== 500),
  );
};

export const getSubvention = (globalState: StoreState): BonusReward | null | undefined => {
  if (isMainInsuredActive(globalState)) {
    return globalState.bonusShop.bonusRewards.find(
      (reward) => reward.articleNumber === '9609' && reward.type === SUBVENTION_REWARD,
    );
  }
  return globalState.bonusShop.bonusRewards.find(
    (reward) => reward.articleNumber === '9611' && reward.type === SUBVENTION_REWARD,
  );
};

export const descriptionOfRewardCategory = (constant: RewardCategory): string => {
  return Strings.bonusShop.bonusRewardCategory[constant]
    ? Strings.bonusShop.bonusRewardCategory[constant]
    : Strings.bonusShop.unknownCategory;
};

export const hasMultiplePointVersions = (reward: BonusReward): boolean => {
  return (
    reward.type === MONEY_REWARD || reward.type === DONATION_REWARD || reward.type === GIFT_REWARD
  );
};

export const resetBonusRewards = (): Action => {
  return {
    type: RESET_BONUS_REWARDS,
  };
};

export const hasNoPhysicalRewards = (globalState: StoreState) => {
  return globalState.bonusShop.bonusRewards.every((reward) => reward.type !== PHYSICAL_REWARD);
};
